import type { DeepReadonly, Ref } from 'vue'
import type { PerspectiveCamera, Scene } from 'three'
import type { Drill } from '~/entities/drill'
import type { DrillDataLocalization, DrillDataSet, DrillDataSetAggregate, DrillLayer, DrillCSVFile } from '~/entities/drillLabel'
import type { DrillHoles, DrillHoleApiData, DrillDataConfig } from '~/entities/drillHole'
import type { ModelView } from '~/entities/meeting'
import type { DrillLabelVisibility, ClusterVisibility, NodeVisibility, LabelVisibility } from '~/entities/model3d'
import type { ApiListViewWithDrillData } from '~/entities/api'
import { DrillLabelManager } from '~/lib/three-d-resources/drill-label-manager'
import { disposeScene } from '~/lib/three-d-viewer/utils'
import { DEFAULT_FONT_SIZE } from '../constants/labels'
import { DrillHoleManager } from './drill-hole-manager'
import { DrillLegendManager } from './drill-legend-manager'
import { DoraClusterManager } from './dora-cluster-manager'
import { DrillConfigurationManager } from './drill-configuration-manager'

export interface DrillLayerService {
  getLayerListItems(drillDataSetId: number): DrillLayer[]
}

export interface ConfigProvider {
  getConfigs(sceneConfigs: DrillDataConfig[]): DrillDataConfig[]
}

export class DrillDataSetManager {
  public drillLabelManager: DrillLabelManager
  public drillHoleManager: DrillHoleManager
  public drillLegendManager: DrillLegendManager
  public drillConfigurationManager: DrillConfigurationManager
  public doraClusterManager: DoraClusterManager
  public drillDataSets: DrillDataSet[] = []
  private holes: Map<number, DrillHoles> = new Map()
  private modelId: number | null = null
  private sceneId: number | null = null
  private companyId: number | null = null
  private mode: 'view' | 'edit' = 'view'
  private paginationLimit = 10000

  constructor(
    private loaderConfig: { uuid: string | null },
    private onLabelsChanged: () => void,
    onVisibilityChanged?: (param: NodeVisibility[] | LabelVisibility[] | DrillLabelVisibility[] | ClusterVisibility[]) => void,
    onToggleCollapsed?: (payload: { id: number; is_collapsed: boolean }) => void,
    currentView?: Ref<DeepReadonly<ModelView>>,
    maxAge: number = 0,
  ) {
    this.drillLabelManager = new DrillLabelManager(
      this.mode,
      loaderConfig,
      onLabelsChanged,
      onVisibilityChanged,
      onToggleCollapsed,
      currentView,
      maxAge,
    )
    this.drillHoleManager = new DrillHoleManager(loaderConfig)
    this.drillConfigurationManager = new DrillConfigurationManager(loaderConfig)
    this.drillLegendManager = new DrillLegendManager(onLabelsChanged)
    this.doraClusterManager = new DoraClusterManager(loaderConfig, this.drillLabelManager, onLabelsChanged, onVisibilityChanged)
  }

  get hasUnprocessedDataSet(): boolean {
    return this.drillDataSets.some(d => d.data_set.hole_data_status !== 'ready')
  }

  async loadDrillDataSet({ companyId, modelId }: { companyId: number; modelId: number }) {
    let dataSets: DrillDataSet[]

    try {
      this.modelId = modelId
      this.companyId = companyId

      dataSets = await this.getDataSets({ companyId, modelId })
    } catch {
      dataSets = []
    }

    if (this.modelId !== modelId) return

    this.drillDataSets = dataSets
    await this.fetchAllDataSets()
  }

  async getDataSets({ companyId, modelId }: { companyId: number; modelId: number }) {
    return await $fetch<DrillDataSet[]>(`/v2/drill/companies/${companyId}/mappers`, {
      ...useFetchSharedOptions(),
      headers: {
        ...useFetchSharedOptions().headers,
        'X-Deck-Auth-UUID': `${this.loaderConfig.uuid}`,
      },
      params: {
        sort_by: '-created_at',
        include: ['data_set', 'localization'],
        model_id: modelId,
      },
    })
  }

  async updateDataSets({
    companyId,
    drillDataSets,
    drillDataSetUpdates,
  }: {
    companyId: number
    drillDataSets: DrillDataSet[]
    drillDataSetUpdates: Partial<Drill>
  }) {
    await Promise.all(
      drillDataSets.map(drill =>
        $fetch(`/v2/drill/companies/${companyId}/data/${drill.data_set_id}`, {
          ...useFetchSharedOptions(),
          method: 'PATCH',
          body: drillDataSetUpdates,
        }),
      ),
    )
  }

  async addDataSet({ drillDataSet, localization }: { drillDataSet: Drill; localization: DrillDataLocalization }) {
    if (!this.modelId) return

    const newDataSet = {
      data_set_id: drillDataSet.id,
      data_set: drillDataSet,
      localization_id: localization.id,
      localization,
      model_id: this.modelId,
    }

    this.drillDataSets = [newDataSet].concat(this.drillDataSets.filter(({ data_set_id }) => data_set_id !== newDataSet.data_set_id))

    await this.drillLabelManager.addLabelsForDataSet({ drillDataSet: newDataSet })
    await this.addHolesForDataSet(this.drillDataSets)
    await this.doraClusterManager.fetchClusters(this.drillDataSets)

    this.drillConfigurationManager.addConfigsForScene(this._getSceneDefaultConfigs(this.sceneId))
    const { sceneConfigs } = this.drillConfigurationManager
    this.doraClusterManager.createClusterLabels(newDataSet)
    this.drillLegendManager.registerLegends(sceneConfigs as DrillDataConfig[])
    this.setActiveLegend(this.drillLegendManager.getLegendList()[0]?.id)

    this.onLabelsChanged()
  }

  async removeDataSet(dataSetId: number) {
    this.drillDataSets = this.drillDataSets.filter(dataSet => dataSet.data_set_id !== dataSetId)

    this.drillLabelManager.removeLabelsForDataSet(dataSetId)
    this.drillLegendManager.removeLegendForDataSet(dataSetId)
    this.doraClusterManager.removeClustersForDataSet(dataSetId)
    this.removeDrillHolesForDataSet(dataSetId)
    this.drillConfigurationManager.removeConfigsForDataSet(dataSetId)
    this.onLabelsChanged()
  }

  private async fetchAllDataSets() {
    if (!this.modelId) return

    await this.drillLabelManager.fetchLabels({ modelId: this.modelId, drillDataSets: this.drillDataSets })
    await this.fetchHoles({ modelId: this.modelId, drillDataSets: this.drillDataSets })
    await this.doraClusterManager.fetchClusters(this.drillDataSets)
  }

  async setSceneId(sceneId: number | null) {
    this.sceneId = sceneId

    await this.drillConfigurationManager.setConfigsForScene(sceneId, this._getSceneDefaultConfigs(sceneId))
    const { sceneConfigs } = this.drillConfigurationManager
    this.drillLegendManager.setLegendsForScene(sceneConfigs as DrillDataConfig[])
    await this.doraClusterManager.setClustersForScene(sceneConfigs as DrillDataConfig[], this.drillDataSets)
    this.drillLabelManager.setSceneId(sceneId)
    this.drillHoleManager.createAllDrillHoles(this.holes, this.drillLegendManager.registeredLegends, sceneConfigs as DrillDataConfig[])
  }

  setSceneIdToCurrent() {
    return this.drillLabelManager.setSceneId(this.sceneId)
  }

  getDrillDataSetAggregate(drillDataSetId: number): DrillDataSetAggregate {
    const dataSet = this.drillDataSets.find(({ data_set_id }) => data_set_id === drillDataSetId)

    if (!dataSet) throw new Error(`Data set ${drillDataSetId} not found`)

    const drillLegendLayers = this.drillLegendManager.getLayerListItems(drillDataSetId)
    const drillLabelLayers = this.drillLabelManager.getLayerListItems(drillDataSetId)
    const clusterLayers = this.doraClusterManager.getLayerListItems(drillDataSetId)
    const drillHoleAndLabelLayers = this._getDrillHoleAndLabelLayers(drillDataSetId, drillLabelLayers)

    return { ...dataSet, drillLayers: [...clusterLayers, ...drillLegendLayers, ...drillHoleAndLabelLayers] }
  }

  private _getDrillHoleAndLabelLayers(drillDataSetId: number, drillLabelLayers: DrillLayer[]): DrillLayer[] {
    const drillApiData = this.holes.get(drillDataSetId)

    if (!drillApiData) return drillLabelLayers

    const layersWithoutFiles = (drillApiData.data || []).map(
      hole =>
        ({
          id: hole.hole_id,
          type: 'drill',
          title: hole.hole_id,
          visible: true,
          files: [] as DrillCSVFile[],
        }) as DrillLayer,
    )
    const layersWithFileIds = new Set(drillLabelLayers.map(item => item.id))

    return drillLabelLayers.concat(layersWithoutFiles.filter(item => !layersWithFileIds.has(item.id as string)))
  }

  async fetchHoles({ modelId, drillDataSets }: { modelId: number; drillDataSets: DrillDataSet[] }) {
    this.modelId = modelId

    if (this.modelId !== modelId) return // avoid race condition

    this.holes = await this._getDrillHoleMap(drillDataSets)
  }

  async addHolesForDataSet(drillDataSets: DrillDataSet[]) {
    this.holes = await this._getDrillHoleMap(drillDataSets)
    this.drillHoleManager.createAllDrillHoles(this.holes, this.drillLegendManager.registeredLegends)
  }

  getHolesForDrillDataSet(drillDataSetId: number): DrillHoles | undefined {
    return this.holes.get(drillDataSetId)
  }

  removeDrillHolesForDataSet(drillDataSetId: number) {
    this.holes.delete(drillDataSetId)
    this.drillHoleManager.createAllDrillHoles(this.holes, this.drillLegendManager.registeredLegends)
  }

  private async _getDrillHoleMap(drillDataSets: DrillDataSet[]): Promise<Map<number, DrillHoles>> {
    const drillTuples: [number, DrillHoles][] = (await Promise.all(
      drillDataSets.map(async dataSet => {
        try {
          const [drillHoles, drillConfig] = await Promise.all([
            $fetch<ApiListViewWithDrillData<DrillHoleApiData>>(`/v2/drill/data/${dataSet.data_set_id}/hole_data`, {
              ...useFetchSharedOptions(),
              headers: {
                ...useFetchSharedOptions().headers,
                'X-Deck-Auth-UUID': `${this.loaderConfig.uuid}`,
              },
              params: { limit: this.paginationLimit },
            }),
            $fetch<DrillDataConfig[]>(`/v2/drill/data/${dataSet.data_set_id}/configuration`, {
              ...useFetchSharedOptions(),
              headers: {
                ...useFetchSharedOptions().headers,
                'X-Deck-Auth-UUID': `${this.loaderConfig.uuid}`,
              },
            }),
          ])

          return [
            dataSet.data_set_id,
            {
              data: drillHoles.results,
              count: drillHoles.count,
              categorical_from_to_max_length: drillHoles.categorical_from_to_max_length,
              numerical_from_to_max_length: drillHoles.numerical_from_to_max_length,
              config: drillConfig,
              localization: dataSet.localization,
              next: drillHoles.next,
            },
          ]
        } catch (e) {
          return [dataSet.data_set_id, { data: [], config: [], localization: undefined }]
        }
      }),
    )) as [number, DrillHoles][]

    return new Map(drillTuples)
  }

  getParentDataSet(drillLabelId: number): DrillDataSet | null {
    let parentDataSet: DrillDataSet | null = null
    this.drillDataSets.forEach(dataSet => {
      const labels = this.drillLabelManager.getLabelsForDrillDataSet(dataSet.data_set_id)
      if (labels.find(label => label.id === drillLabelId)) {
        parentDataSet = dataSet
      }
    })
    return parentDataSet
  }

  getLabelsTextSize(dataSetId: number): number {
    const labels = this.drillLabelManager.getLabelsForDrillDataSet(dataSetId)
    if (!labels.length) return DEFAULT_FONT_SIZE

    const container = this.drillLabelManager.getDrillLabelObject(labels[0].id)
    if (!container) return DEFAULT_FONT_SIZE

    if (container.auto_scale) return container.autoScaleSize

    const fontSize = container.textLabelContainer.style.fontSize.match(/\d+/)
    return fontSize?.length ? parseInt(fontSize[0], 10) : DEFAULT_FONT_SIZE
  }

  setLabelsTextSize(dataSetId: number, newSize: number): void {
    this.drillLabelManager.setFontSize(dataSetId, newSize)
  }

  setLabelsAutoScale(dataSetId: number, autoScale: boolean): void {
    this.drillLabelManager.setAutoScale(dataSetId, autoScale)
  }

  setDrillLabelState(id: number, state: { is_collapsed: boolean; is_image_collapsed: boolean }) {
    if (typeof state.is_collapsed === 'boolean') {
      this.drillLabelManager.toggleCollapsed(id, state.is_collapsed)
    }
  }

  toggleAllCollapsed(dataSetId: number): void {
    this.drillLabelManager.toggleAllCollapsed(dataSetId)
  }

  async updateServer(sceneId: number) {
    const mappings = await $fetch<Array<{ id: number; data_set_id: number; localization_id: number }>>(
      `/v2/drill/companies/${this.companyId}/mappers`,
      {
        ...useFetchSharedOptions(),
        headers: {
          ...useFetchSharedOptions().headers,
          'X-Deck-Auth-UUID': `${this.loaderConfig.uuid}`,
        },
        params: { model_id: this.modelId },
      },
    )

    const toBeAdded = this.drillDataSets
      .filter(
        ({ data_set_id, localization_id }) =>
          !mappings.find(mapping => mapping.data_set_id === data_set_id && mapping.localization_id === localization_id),
      )
      .map(dataSet => ({
        data_set_id: dataSet.data_set_id,
        localization_id: dataSet.localization_id,
        model_id: this.modelId,
      }))

    const toBeDeleted = mappings.filter(
      mapping =>
        !this.drillDataSets.find(
          ({ data_set_id, localization_id }) => data_set_id === mapping.data_set_id && localization_id === mapping.localization_id,
        ),
    )

    await Promise.all([
      toBeAdded.length
        ? $fetch(`/v2/drill/companies/${this.companyId}/mappers`, { ...useFetchSharedOptions(), method: 'POST', body: toBeAdded })
        : null,
      toBeDeleted.length
        ? $fetch(`/v2/drill/companies/${this.companyId}/mappers`, { ...useFetchSharedOptions(), method: 'DELETE', body: toBeDeleted })
        : null,

      this.drillLabelManager.updateServer(sceneId),
      this.drillConfigurationManager.updateSceneConfigs(sceneId, this.modelId as number, this._getDrillConfigurations()),
    ])
  }

  private _getDrillConfigurations(): DrillDataConfig[] {
    const { sceneConfigs } = this.drillConfigurationManager
    const configsForLegends = this.drillLegendManager.getConfigs(sceneConfigs as DrillDataConfig[])
    const configsForClusters = this.doraClusterManager.getConfigs(sceneConfigs as DrillDataConfig[])
    const configurations = [...configsForClusters, ...configsForLegends]

    return configurations.length ? configurations : (sceneConfigs as DrillDataConfig[])
  }

  private _getSceneDefaultConfigs(sceneId: number | null): DrillDataConfig[] {
    const drillDefaultConfigs = Array.from(this.holes.values())
      .flatMap(hole => hole.config)
      .map(config => ({ ...config, camera_id: sceneId }))
    const clusterDefaultConfigs = this.drillDataSets.map(dataSet => {
      const clusters = this.doraClusterManager.getDefaultClusters(dataSet.data_set_id)

      if (!clusters.length) return null

      return {
        camera_id: sceneId,
        data_set_id: dataSet.data_set_id,
        collar: null,
        survey: null,
        numerical_assay: null,
        categorical_assay: null,
        highlight: null,
        visible_hole_ids: [],
        clusters,
      }
    })

    return [...drillDefaultConfigs.filter(Boolean), ...clusterDefaultConfigs.filter(Boolean)] as DrillDataConfig[]
  }

  getCompanyId() {
    return this.companyId
  }

  getModelId() {
    return this.modelId
  }

  mount(labelContainer: HTMLElement) {
    this.drillLabelManager.mount(labelContainer)
  }

  toggleGroupVisibility(visible: boolean) {
    this.drillLabelManager.group.visible = visible
  }

  cleanUp() {
    this.drillDataSets = []
    this.modelId = null
    this.drillLabelManager.resetData()
    this.drillHoleManager.cleanUp()
    this.doraClusterManager.cleanUp()
    disposeScene(this.drillLabelManager.group)
  }

  render(scene: Scene, camera: PerspectiveCamera) {
    this.drillLabelManager.render(scene, camera)
  }

  setSize(width: number, height: number) {
    this.drillLabelManager.setSize(width, height)
  }

  setMode(mode: 'view' | 'edit') {
    this.mode = mode
    this.drillLabelManager.setMode(mode)
  }

  setActiveLegend(legendId: string) {
    const { sceneConfigs } = this.drillConfigurationManager
    const dataSetId = Number(legendId.split('-')[0])
    const holes = this.holes.get(dataSetId)
    const legend = this.drillLegendManager.getLegendById(legendId)

    if (!holes || !legend) return

    this.drillLegendManager.setActiveLegend(legendId)
    this.drillHoleManager.setActiveAssay(dataSetId, holes, this.drillLegendManager.registeredLegends, sceneConfigs as DrillDataConfig[])
  }
}
